import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { Box, Tooltip } from "@mui/material";
import Avatar from "../../profile/Avatar";

export interface PlayerIdentity {
  email: string;
  displayName?: string;
  avatarSrc?: string;
}

export type PlayerDirectory = Record<string, PlayerIdentity>;

export const playerIdentity = (
  email: string,
  directory?: PlayerDirectory,
): PlayerIdentity => {
  const match = directory?.[email.toLowerCase()] || directory?.[email];
  return match || { email, displayName: email };
};

export const playerLabel = (player: PlayerIdentity) =>
  player.displayName?.trim() || player.email;

export const PlayerAvatar = ({
  player,
  size = 28,
  alt,
}: {
  player: PlayerIdentity;
  size?: number;
  alt?: string;
}) => (
  <Avatar
    email={player.email}
    auth0Picture={player.avatarSrc}
    alt={alt || playerLabel(player)}
    showSkeleton={false}
    sx={{
      width: size,
      height: size,
      bgcolor: "surface.highest",
      color: "text.secondary",
      border: "2px solid",
      borderColor: "surface.lowest",
      fontSize: size * 0.62,
    }}
  >
    <PersonRoundedIcon fontSize="inherit" />
  </Avatar>
);

export const PlayerAvatarStack = ({
  players,
  teamName,
  align = "flex-start",
  max = 4,
}: {
  players: PlayerIdentity[];
  teamName: string;
  align?: "flex-start" | "flex-end";
  max?: number;
}) => {
  const hasOverflow = players.length > max + 1;
  const visible = hasOverflow ? players.slice(0, max) : players;
  const remaining = hasOverflow ? players.slice(max) : [];
  const ordered = align === "flex-end" ? [...visible].reverse() : visible;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: align,
        flexDirection: align === "flex-end" ? "row-reverse" : "row",
        minHeight: 28,
      }}
      role="group"
      aria-label={`${players.length} players picked ${teamName}`}
    >
      {ordered.map((player, index) => (
        <Tooltip
          key={player.email}
          title={`${playerLabel(player)} picked ${teamName}`}
          arrow
        >
          <Box
            component="button"
            type="button"
            aria-label={`${playerLabel(player)} picked ${teamName}`}
            sx={{
              display: "inline-flex",
              p: 0,
              ml:
                align === "flex-start" && index > 0
                  ? "-6px"
                  : 0,
              mr:
                align === "flex-end" && index > 0
                  ? "-6px"
                  : 0,
              borderRadius: "50%",
              border: 0,
              bgcolor: "transparent",
              cursor: "help",
              outlineOffset: 2,
            }}
          >
            <PlayerAvatar player={player} />
          </Box>
        </Tooltip>
      ))}
      {remaining.length > 0 && (
        <Tooltip
          arrow
          title={remaining
            .map((player) => `${playerLabel(player)} picked ${teamName}`)
            .join(", ")}
        >
          <Box
            component="button"
            type="button"
            aria-label={`Show ${remaining.length} more players who picked ${teamName}`}
            sx={{
              width: 28,
              height: 28,
              p: 0,
              ml: align === "flex-start" ? "-6px" : 0,
              mr: align === "flex-end" ? "-6px" : 0,
              borderRadius: "50%",
              border: "2px solid",
              borderColor: "surface.lowest",
              bgcolor: "surface.highest",
              color: "text.primary",
              font: "inherit",
              fontSize: 10,
              fontWeight: 800,
              cursor: "help",
            }}
          >
            +{remaining.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
